import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Compass,
  Heart,
  ShieldCheck,
  Leaf,
  Brain,
  Layers,
  MessageSquareText,
  Boxes,
  Wand2,
  Rocket,
  Mail,
  Phone,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { BRAND, whatsappLink } from "@/lib/brand";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const pillarIcons = [Sparkles, Brain, Compass, ShieldCheck, Leaf];
const serviceIcons = [Compass, Layers, MessageSquareText, Boxes, Wand2, Rocket];

function Eyebrow({ children, dark = false }) {
  return (
    <p
      className="inline-block text-[11px] font-display font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full"
      style={
        dark
          ? { background: "var(--brand-accent)", color: "#000" }
          : { background: "var(--brand-surface-soft)", color: "var(--brand-primary)" }
      }
    >
      {children}
    </p>
  );
}

function SectionTitle({ eyebrow, title, description, dark }) {
  return (
    <div className="max-w-3xl">
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2
        className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight"
        style={dark ? { color: "#fff" } : undefined}
      >
        {title}
      </h2>
      {description && (
        <p
          className="mt-4 text-base sm:text-lg leading-relaxed"
          style={{ color: dark ? "rgba(255,255,255,0.85)" : "#3a4659" }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

function HeroVisual({ chips }) {
  return (
    <div
      className="relative rounded-3xl p-7 md:p-9 backdrop-blur-md overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.07)",
        borderColor: "rgba(255,255,255,0.18)",
        border: "1px solid rgba(255,255,255,0.18)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
      }}
      data-testid="hero-visual"
    >
      {/* Decorative concentric circles using brand colors */}
      <div className="relative aspect-[4/3] w-full">
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30"
          style={{ width: "92%", paddingTop: "92%", background: "radial-gradient(circle, var(--brand-accent) 0%, transparent 70%)" }}
        />
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border opacity-40"
          style={{ width: "70%", paddingTop: "70%", borderColor: "rgba(255,255,255,0.4)" }}
        />
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border opacity-60"
          style={{ width: "44%", paddingTop: "44%", borderColor: "var(--brand-accent)" }}
        />

        {/* Floating geometric chips */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="grid grid-cols-2 gap-3 max-w-[260px]">
            {chips.slice(0, 4).map((c, i) => (
              <span
                key={c}
                className="text-[11px] font-display font-semibold px-3 py-2 rounded-full text-center"
                style={{
                  background:
                    i % 2 === 0 ? "var(--brand-accent)" : "rgba(255,255,255,0.95)",
                  color: i % 2 === 0 ? "#000" : "var(--brand-primary)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
                }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t" style={{ borderColor: "rgba(255,255,255,0.18)" }}>
        <p className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] opacity-80 text-white">
          AI · Strategy · Identity
        </p>
        <p className="text-white text-sm mt-2 opacity-90 leading-relaxed">
          One coherent brand system across product, marketing and editorial.
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const { t } = useLanguage();
  const h = t.home;

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        id="overview"
        className="relative grain-texture overflow-hidden"
        style={{ background: "var(--brand-primary)", color: "#fff" }}
        data-testid="hero-section"
      >
        <div
          aria-hidden
          className="hidden md:block absolute -right-40 -top-32 w-[520px] h-[520px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, var(--brand-accent) 0%, transparent 65%)" }}
        />
        <div
          aria-hidden
          className="hidden md:block absolute -left-24 -bottom-24 w-[320px] h-[320px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #fff 0%, transparent 60%)" }}
        />
        <div className="container-brand pt-16 pb-20 md:pt-24 md:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 reveal-up">
              <Eyebrow dark>{h.hero.eyebrow}</Eyebrow>
              <h1
                className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
                style={{ color: "#fff" }}
              >
                {h.hero.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed opacity-90">
                {h.hero.description}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href={whatsappLink(t.whatsapp.message)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-display font-semibold text-sm transition-all duration-200 hover:-translate-y-[1px]"
                  style={{ background: "var(--brand-accent)", color: "#000" }}
                >
                  <WhatsAppIcon size={16} /> {h.hero.primaryCta}
                </a>
                <Link
                  to="/services"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-display font-semibold text-sm border transition-colors"
                  style={{
                    borderColor: "rgba(255,255,255,0.6)",
                    color: "#fff",
                    background: "transparent",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.1)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {h.hero.secondaryCta} <ArrowRight size={16} />
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="hero-chips">
                {h.hero.chips.map((chip) => (
                  <span
                    key={chip}
                    className="text-xs font-display font-medium px-3 py-1.5 rounded-full"
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.16)",
                      color: "#fff",
                    }}
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 reveal-up">
              <HeroVisual chips={h.hero.chips} />
              <div
                className="mt-5 rounded-2xl p-5"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.14)",
                }}
              >
                <p className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] opacity-80 text-white">
                  {h.hero.statBlock.label}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3">
                  {h.hero.statBlock.rows.map((r) => (
                    <li key={r.value} className="text-white">
                      <p className="font-display font-bold text-base leading-tight">{r.value}</p>
                      <p className="text-[11px] opacity-75 mt-1 leading-snug">{r.caption}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-white" data-testid="pillars-section">
        <div className="container-brand py-16 md:py-24">
          <SectionTitle eyebrow={h.pillars.eyebrow} title={h.pillars.title} />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {h.pillars.items.map((p, i) => {
              const Icon = pillarIcons[i] || Sparkles;
              return (
                <div
                  key={p.title}
                  className="rounded-2xl p-5 border bg-white transition-all duration-200 hover:-translate-y-[2px]"
                  style={{
                    borderColor: "var(--brand-border-soft)",
                    boxShadow: "var(--brand-shadow-card)",
                  }}
                  data-testid={`pillar-${i}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
                    style={{ background: "var(--brand-surface-soft)", color: "var(--brand-primary)" }}
                  >
                    <Icon size={18} />
                  </span>
                  <p className="mt-4 font-display font-bold text-[color:var(--brand-primary)] leading-tight">
                    {p.title}
                  </p>
                  <p className="mt-2 text-sm text-[#3a4659] leading-relaxed">{p.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="services-preview"
        style={{ background: "var(--brand-surface-soft)" }}
        data-testid="services-section"
      >
        <div className="container-brand py-16 md:py-24">
          <SectionTitle
            eyebrow={h.services.eyebrow}
            title={h.services.title}
            description={h.services.description}
          />

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {h.services.items.map((s, i) => {
              const Icon = serviceIcons[i] || Compass;
              return (
                <div
                  key={s.title}
                  className="group rounded-2xl bg-white p-6 border transition-all duration-200 hover:-translate-y-[3px]"
                  style={{
                    borderColor: "var(--brand-border-soft)",
                    boxShadow: "var(--brand-shadow-card)",
                  }}
                  data-testid={`service-${i}`}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "var(--brand-shadow-floating)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "var(--brand-shadow-card)")}
                >
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-xl"
                    style={{ background: "var(--brand-accent)", color: "#000" }}
                  >
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-lg text-[color:var(--brand-primary)] leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#3a4659] leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <Link
              to="/services"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-display font-semibold text-sm text-white transition-all hover:-translate-y-[1px]"
              style={{ background: "var(--brand-primary)" }}
            >
              {h.services.cta} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section style={{ background: "var(--brand-primary)" }} className="grain-texture text-white" data-testid="process-section">
        <div className="container-brand py-16 md:py-24">
          <SectionTitle eyebrow={h.process.eyebrow} title={h.process.title} dark />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {h.process.steps.map((s, i) => (
              <div
                key={s.step}
                className="rounded-2xl p-6"
                style={{
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}
                data-testid={`process-step-${i}`}
              >
                <span
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-display font-bold"
                  style={{ background: "var(--brand-accent)", color: "#000" }}
                >
                  {s.step}
                </span>
                <p className="mt-4 font-display font-bold text-lg text-white">{s.title}</p>
                <p className="mt-2 text-sm opacity-90 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="bg-white" data-testid="audiences-section">
        <div className="container-brand py-16 md:py-24">
          <SectionTitle eyebrow={h.audiences.eyebrow} title={h.audiences.title} />
          <div className="mt-8 flex flex-wrap gap-2.5">
            {h.audiences.items.map((item) => (
              <span
                key={item}
                className="text-sm font-display font-medium px-4 py-2 rounded-full border"
                style={{
                  borderColor: "var(--brand-border-soft)",
                  background: "var(--brand-surface-soft)",
                  color: "var(--brand-primary)",
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "var(--brand-accent)" }} data-testid="cta-section">
        <div className="container-brand py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-black">
                {h.cta.title}
              </h2>
              <p className="mt-4 text-base sm:text-lg text-black/80 leading-relaxed max-w-2xl">
                {h.cta.description}
              </p>
            </div>
            <div className="md:col-span-4 flex md:flex-col gap-3 md:items-end">
              <a
                href={whatsappLink(t.whatsapp.message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-display font-semibold text-sm text-white transition-all hover:-translate-y-[1px]"
                style={{ background: "var(--brand-primary)" }}
              >
                <WhatsAppIcon size={16} /> {h.cta.primary}
              </a>
              <a
                href={`tel:${BRAND.phoneHref}`}
                data-testid="cta-phone"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-display font-semibold text-sm border bg-white"
                style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
              >
                <Phone size={16} /> {h.cta.secondary}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
